import React, { useState, useRef, useEffect } from 'react';
import { Users, X, LogIn } from 'lucide-react';
import { StoredUserSession, UserSessionStorage } from '../services/user-session-storage';
import { useAuth } from '../hooks/use-auth';
import { AddAccountModal } from './add-account-modal';
import { isAccountSwitchingEnabled } from '../config/oidc-config';

interface UserSwitcherProps {
  className?: string;
}

export function UserSwitcher({ className = '' }: UserSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [sessions, setSessions] = useState<StoredUserSession[]>([]);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { tokenClaims, signIn, signOut, isAuthenticated } = useAuth();
  const accountSwitchingEnabled = isAccountSwitchingEnabled();

  // Load stored sessions
  useEffect(() => {
    const currentUsername = tokenClaims?.preferred_username;
    const recentSessions = UserSessionStorage.getRecentSessions(currentUsername);
    setSessions(recentSessions);
    
    // Debug logging
    console.log('[UserSwitcher] Loaded sessions:', {
      currentUsername,
      totalSessions: UserSessionStorage.getSessions().length,
      recentSessions: recentSessions.length,
      sessions: recentSessions
    });
    
    // CRITICAL: Check if we just added a new account
    // If so, we should NOT automatically switch away from the newly added account
    const addingAccount = sessionStorage.getItem('approvn_adding_account');
    if (addingAccount === 'true') {
      console.warn('[UserSwitcher] ⚠️ Account addition in progress - preventing automatic session switches');
      return;
    }
  }, [tokenClaims, isOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleSwitchUser = async (session: StoredUserSession) => {
    try {
      console.log('========================================');
      console.log('[UserSwitcher] 🔄 STARTING USER SWITCH');
      console.log('========================================');
      console.log('[UserSwitcher] Target user:', session.username);
      console.log('[UserSwitcher] Current user:', tokenClaims?.preferred_username);
      console.log('[UserSwitcher] Session data:', {
        hasOidcUser: !!session.oidcUser,
        hasAccessToken: !!session.oidcUser?.access_token,
        hasExpiresAt: !!session.oidcUser?.expires_at,
        expiresAt: session.oidcUser?.expires_at ? new Date(session.oidcUser.expires_at * 1000).toISOString() : 'N/A',
        profileUsername: session.oidcUser?.profile?.preferred_username
      });
      
      // Check if we have OIDC data at all
      if (!session.oidcUser || !session.oidcUser.access_token) {
        console.warn('[UserSwitcher] ❌ No OIDC data found for user. Session was saved before OIDC support. Need to re-authenticate.');
        
        // Store hint for login form
        sessionStorage.setItem('approvn_switch_to_user', session.username);
        
        // Sign out and redirect to login
        await signOut();
        setIsOpen(false);
        return;
      }
      
      // Check if the stored session is still valid
      const isValid = UserSessionStorage.isSessionValid(session.username);
      console.log('[UserSwitcher] Session valid:', isValid);

      if (isValid) {
        console.log('[UserSwitcher] ✅ Session is valid, performing seamless switch...');
        
        // Seamless switch: restore the OIDC user data
        const switched = UserSessionStorage.switchToSession(session.username);
        
        if (switched) {
          console.log('[UserSwitcher] ✅ Switch successful! Navigating to force re-initialization...');
          console.log('========================================');
          setIsOpen(false);
          
          // Use window.location.href instead of reload to force complete re-initialization
          // This ensures the OIDC library picks up the manually-set localStorage data
          setTimeout(() => {
            window.location.href = window.location.origin;
          }, 50);
        } else {
          console.error('[UserSwitcher] ❌ Switch failed!');
          throw new Error('Failed to switch session');
        }
      } else {
        // Token expired or invalid, need to re-authenticate
        console.log('[UserSwitcher] ⚠️ Session expired, redirecting to login...');
        console.log('========================================');
        
        // Store hint for login form
        sessionStorage.setItem('approvn_switch_to_user', session.username);
        
        // Sign out and redirect to login
        await signOut();
        setIsOpen(false);
      }
    } catch (error) {
      console.error('[UserSwitcher] Failed to switch user:', error);
      
      // Fallback to re-authentication
      sessionStorage.setItem('approvn_switch_to_user', session.username);
      await signOut();
      setIsOpen(false);
    }
  };

  const handleRemoveSession = (event: React.MouseEvent, username: string) => {
    event.stopPropagation(); // Prevent triggering switch
    UserSessionStorage.removeSession(username);
    setSessions(prev => prev.filter(s => s.username !== username));
  };

  const handleAddAccount = () => {
    console.log('========================================');
    console.log('[UserSwitcher] 🔐 ADDING NEW ACCOUNT');
    console.log('========================================');
    console.log('[UserSwitcher] Current user:', tokenClaims?.preferred_username);
    
    // Show the instruction modal instead of immediately redirecting
    setIsOpen(false);
    setShowAddAccountModal(true);
  };

  const proceedWithAddAccount = () => {
    // Store a flag indicating we're adding an account (not signing out)
    sessionStorage.setItem('approvn_adding_account', 'true');
    
    // Store the current username so we know who was logged in
    if (tokenClaims?.preferred_username) {
      sessionStorage.setItem('approvn_previous_user', tokenClaims.preferred_username);
      console.log('[UserSwitcher] Stored previous user:', tokenClaims.preferred_username);
    }
    
    console.log('[UserSwitcher] Initiating login for new account...');
    setShowAddAccountModal(false);
    
    // Start the OAuth flow for a new user with forced login
    signIn(true);  // Pass true to force new login
  };

  // Prefers the richer cached oidcUser.profile (ID token/userinfo) over the flat name fields
  // captured at save time, so sessions saved before that data was reliably available self-heal.
  const getDisplayName = (session: StoredUserSession): string => {
    const profile = session.oidcUser?.profile;
    const givenName = profile?.given_name || session.given_name;
    const familyName = profile?.family_name || session.family_name;
    return profile?.name || session.name || `${givenName || ''} ${familyName || ''}`.trim() || session.username;
  };

  const getInitials = (session: StoredUserSession): string => {
    const profile = session.oidcUser?.profile;
    const givenName = profile?.given_name || session.given_name;
    const familyName = profile?.family_name || session.family_name;
    if (givenName && familyName) {
      return `${givenName[0]}${familyName[0]}`.toUpperCase();
    }
    const name = profile?.name || session.name;
    if (name) {
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return name[0].toUpperCase();
    }
    return session.username[0].toUpperCase();
  };

  const formatLastLogin = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (!accountSwitchingEnabled) {
    return null;
  }

  // Don't show if not authenticated
  if (!isAuthenticated) {
    // When not authenticated, check if there are any saved sessions
    const allSessions = UserSessionStorage.getSessions();
    if (allSessions.length === 0) {
      return null;
    }
  }

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-secondary-200 bg-white dark:bg-secondary-800 border border-gray-300 dark:border-secondary-600 rounded-md hover:bg-gray-50 dark:hover:bg-secondary-700 transition-colors"
        title="Switch account"
      >
        <Users className="h-4 w-4" />
        <span className="hidden sm:inline">Switch Account</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-secondary-800 rounded-lg shadow-lg border border-gray-200 dark:border-secondary-700 py-2 z-50">
          <div className="px-4 py-2 border-b border-gray-200 dark:border-secondary-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Switch Account</h3>
            <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
              {sessions.length === 0 
                ? 'No other accounts saved yet' 
                : 'Select a previously logged-in account'}
            </p>
          </div>

          {sessions.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-gray-500 dark:text-secondary-400 mb-4">
                Sign in with different accounts to see them listed here.
              </p>
              <p className="text-xs text-gray-500 dark:text-secondary-400">
                (Sessions without valid tokens are automatically removed)
              </p>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              {sessions.map((session) => {
                const isValid = UserSessionStorage.isSessionValid(session.username);
                const hasOidcData = !!(session.oidcUser && session.oidcUser.access_token);
                
                return (
                  <div
                    key={session.username}
                    className="group relative hover:bg-gray-50 dark:hover:bg-secondary-700 transition-colors"
                  >
                    <button
                      onClick={() => handleSwitchUser(session)}
                      className="w-full flex items-center px-4 py-3 text-left"
                    >
                      {/* Avatar */}
                      <div className={`h-10 w-10 bg-gradient-to-br ${isValid && hasOidcData ? 'from-blue-500 to-blue-600' : 'from-gray-400 to-gray-500'} rounded-full flex items-center justify-center flex-shrink-0 relative`}>
                        <span className="text-sm font-semibold text-white">
                          {getInitials(session)}
                        </span>
                        {isValid && hasOidcData && (
                          <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 bg-green-500 border-2 border-white rounded-full" title="Session active" />
                        )}
                      </div>

                      {/* User Info */}
                      <div className="ml-3 flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {getDisplayName(session)}
                          {(!isValid || !hasOidcData) && <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">(expired)</span>}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-secondary-400 truncate">{session.email}</p>
                        <p className="text-xs text-gray-400 dark:text-secondary-500 mt-0.5">
                          Last login: {formatLastLogin(session.lastLoginAt)}
                        </p>
                      </div>

                      {/* Login Icon */}
                      <div className="ml-2 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <LogIn className="h-4 w-4 text-gray-400 dark:text-secondary-500" />
                      </div>
                    </button>

                    {/* Remove Button */}
                    <button
                      onClick={(e) => handleRemoveSession(e, session.username)}
                      className="absolute right-2 top-3 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-secondary-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove from list"
                    >
                      <X className="h-4 w-4 text-gray-500 dark:text-secondary-400" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="px-4 py-2 border-t border-gray-200 dark:border-secondary-700 mt-2">
            <button
              onClick={handleAddAccount}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 rounded-md transition-colors"
            >
              <LogIn className="h-4 w-4" />
              <span>Add another account</span>
            </button>
          </div>
        </div>
      )}
      
      {/* Add Account Instruction Modal */}
      <AddAccountModal
        isOpen={showAddAccountModal}
        currentUsername={tokenClaims?.preferred_username}
        onConfirm={proceedWithAddAccount}
        onCancel={() => setShowAddAccountModal(false)}
      />
    </div>
  );
}
